import React, { useEffect, useRef, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { PriorityBadge, SourceBadge, DealTypeBadge } from './Badges';
import { formatAED, initials, formatDateTime } from '@/lib/format';
import { Calendar, MapPin, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export default function ContactCard({ contact, agents = [], onClick }) {
    const navigate = useNavigate();
    const { profile } = useAuth();
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: contact.id, data: { contact } });
    const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
    const agent = agents.find((a) => a.id === contact.assigned_to);
    const [assignOpen, setAssignOpen] = useState(false);
    const assignRef = useRef(null);

    useEffect(() => {
        if (!assignOpen) return;
        const close = (e) => { if (assignRef.current && !assignRef.current.contains(e.target)) setAssignOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [assignOpen]);

    const assignAgent = async (agentId) => {
        setAssignOpen(false);
        const { error } = await supabase
            .from('pipeline_contacts')
            .update({ assigned_to: agentId || null })
            .eq('id', contact.id);
        if (error) toast.error(error.message);
    };

    const open = (e) => {
        if (e.target?.closest?.('[data-no-card-click]')) return;
        if (onClick) onClick(contact);
        else navigate(`/pipeline/${contact.id}`);
    };

    const activeAgents = agents.filter((a) => a.is_active !== false);
    const isAdmin = profile?.role === 'admin';

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`group relative rounded-lg bg-white border border-gray-200 hover:border-aks-gold/60 transition shadow-sm hover:shadow-md cursor-pointer ${isDragging ? 'aks-dragging opacity-90 z-50' : ''}`}
            onClick={open}
            data-testid={`pipeline-card-${contact.id}`}
        >
            <div
                className="absolute top-2.5 right-2.5 h-5 w-5 rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-gray-400 hover:text-aks-gold"
                {...listeners}
                {...attributes}
                data-no-card-click="true"
                title="Drag to move"
                aria-label="Drag handle"
            >
                <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor"><circle cx="2" cy="2" r="1.4"/><circle cx="2" cy="7" r="1.4"/><circle cx="2" cy="12" r="1.4"/><circle cx="8" cy="2" r="1.4"/><circle cx="8" cy="7" r="1.4"/><circle cx="8" cy="12" r="1.4"/></svg>
            </div>
            <div className="p-3.5 pr-7">
                <div className="flex items-center gap-2 mb-1.5">
                    <PriorityBadge value={contact.priority} />
                    {contact.source && <SourceBadge value={contact.source} detail={contact.source_detail} />}
                    {contact.deal_type && <DealTypeBadge value={contact.deal_type} />}
                </div>
                <div className="font-semibold text-[14px] text-aks-text leading-tight truncate">{contact.full_name}</div>
                <div className="text-[12px] text-gray-500 truncate mt-0.5">{contact.phone || contact.email || ''}</div>
                <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px] text-gray-600">
                    {contact.budget_aed != null && (
                        <div className="flex items-center gap-1 truncate"><span className="font-semibold text-aks-navy">{formatAED(contact.budget_aed)}</span></div>
                    )}
                    {contact.property_type && (
                        <div className="flex items-center gap-1 capitalize truncate"><Home className="h-3 w-3" /> {contact.property_type}</div>
                    )}
                    {contact.emirate_preference && (
                        <div className="flex items-center gap-1 truncate col-span-2"><MapPin className="h-3 w-3" /> {contact.emirate_preference}</div>
                    )}
                </div>
                <div className="mt-2.5 flex items-center justify-between">
                    <div className="relative" ref={assignRef} data-no-card-click="true">
                        {isAdmin ? (
                            <button
                                onClick={(e) => { e.stopPropagation(); setAssignOpen((s) => !s); }}
                                className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold transition ${agent ? 'bg-aks-navy text-white hover:ring-2 hover:ring-aks-gold/60' : 'border-2 border-dashed border-gray-300 hover:border-aks-gold text-gray-400'}`}
                                title={agent ? `Assigned: ${agent.full_name} — click to change` : 'Unassigned — click to assign'}
                            >
                                {agent ? initials(agent.full_name) : '+'}
                            </button>
                        ) : (
                            agent ? (
                                <div className="h-6 w-6 rounded-full bg-aks-navy text-white flex items-center justify-center text-[10px] font-semibold" title={agent.full_name}>
                                    {initials(agent.full_name)}
                                </div>
                            ) : (
                                <div className="h-6 w-6 rounded-full border-2 border-dashed border-gray-300" title="Unassigned" />
                            )
                        )}
                        {assignOpen && (
                            <div className="absolute bottom-8 left-0 w-44 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-50">
                                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-semibold border-b border-gray-100">Assign to agent</div>
                                <button
                                    onClick={() => assignAgent(null)}
                                    className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-50 ${!contact.assigned_to ? 'font-semibold text-aks-navy' : 'text-gray-500'}`}
                                >
                                    Unassigned
                                </button>
                                {activeAgents.map((a) => (
                                    <button
                                        key={a.id}
                                        onClick={() => assignAgent(a.id)}
                                        className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-50 ${a.id === contact.assigned_to ? 'font-semibold text-aks-navy' : 'text-aks-text'}`}
                                    >
                                        {a.full_name || a.email}
                                    </button>
                                ))}
                                {activeAgents.length === 0 && (
                                    <div className="px-3 py-2 text-[12px] text-gray-400">No agents found</div>
                                )}
                            </div>
                        )}
                    </div>
                    {contact.next_followup_at && (
                        <div className="flex items-center gap-1 text-[10px] text-gray-500">
                            <Calendar className="h-3 w-3" /> {formatDateTime(contact.next_followup_at)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
